<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from 'vue'
import { assignmentModule } from '@/store/assignment/module'
import UIKit, { type ITableColumn,
    type ITableSortSettings,
    type TCheckboxOption,
    type TTableRow } from '@pocketprep/ui-kit'
import { studentModule } from '@/store/student/module'
import type { Teach } from '@pocketprep/types'
import { instructorModule } from '@/store/instructor/module'
import { examModule } from '@/store/exam/module'
import MultiCheckboxFilter, { type TDropdownCheckboxMenu } from '@/components/Assignments/MultiCheckboxFilter.vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { groupModule } from '@/store/group/module'
import { licenseModule } from '@/store/license/module'
import { router } from '@/router'
import ActionsDropdown from '@/components/ActionsDropdown.vue'
import DeleteAssignmentModal from '@/components/Assignments/DeleteAssignmentModal.vue'
import { formatDate, getAssignmentStatus, setCookie } from '@/utils'
import { toastModule } from '@/store/toast/module'
import { bundleModule } from '@/store/bundle/module'
import { getSessionToken } from '@/store/ParseUtils'
import { userModule } from '@/store/user/module'
import { tableModule } from '@/store/table/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'

const {
    Errors,
    Table,
    TablePagination,
    Tag,
    EmptyState,
    Link: PocketLink,
    Icon,
    Button: PocketButton,
    ModalContainer,
    Modal,
    OverflowTooltip,
} = UIKit

interface IMappedAssignment extends TTableRow {
    id: string
    name: string
    createdBy: string
    creatorFirstName: string
    creatorLastName: string
    numberOfQuestions: number
    studentsCompletedCount: number
    studentsAssignedCount: number
    examName: string
    customGroups: string[]
    status: 'draft' | 'available' | 'upcoming' | 'closed'
    availableDate?: string
    dueDate?: string
    averageScore: string
    averageScoreNumber: number
    shareLink: string
    styles: { [key: string]: string }
}

type TActiveFilters = {
    createdBy: string[]
    exam: string[]
    customGroup: string[]
}

type TAssignmentAction = 'Copy Link' | 'View/Edit' | 'Duplicate' | 'Delete'

const sortSettings = ref<ITableSortSettings>({ column: {
    name: 'Available',
    propName: 'availableDate',
},
direction: 1 })
const activeActionsRowId = ref<string | null>(null)
const assignmentActions: TAssignmentAction[] = [ 'Copy Link', 'View/Edit', 'Duplicate', 'Delete' ]
const showSuccessModal = ref(false)
const showDeleteModal = ref(false)
const assignmentIdToDelete = ref<string | null>(null)
const indexOfOriginalAssignmentByCloneId = ref<{ [cloneId: string]: number }>({})
const brandColors = BrandColors
const isLoading = ref(false)
const activeFilters = ref<TActiveFilters>({ createdBy: [], exam: [], customGroup: [] })
const perPage = 10
const errors = ref<string[]>([])
const currentPage = ref(0)
const rowHoverId = ref<string | number | null>(null)

const assignments = computed(() => {
    return assignmentModule.getters.getCurrentOrgAssignments()
})

const orgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const orgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const yourOrgExamIds = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const orgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const storedAssignment = computed(() => {
    return assignmentModule.state.createdAssignment
})

const orgExams = computed(() => {
    const exams = examModule.getters.getCurrentOrgExams()
    return Object.values(exams)
})

const currentInstructorExams = computed(() => {
    return examModule.getters.getYourOrgExamIds()
})

const orgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const assignmentIdToCustomGroupsLib = computed(() => {
    return Object.values(assignments.value).reduce((acc, assignment) => {
        const assignmentStudentIds = new Set(assignment.assignedOrgStudents?.map(student => student.objectId))
        const customGroups = Object.values(orgGroups.value).filter(group =>
            group.orgStudents.some(student => assignmentStudentIds.has(student.objectId)))
        acc[assignment.objectId] = customGroups.map(customGroup => customGroup.name)
        return acc
    }, {} as { [assignmentId: string]: string[] })
})

const orgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const userIdOrgStudentLib = computed(() => {
    return Object.values(orgStudents.value).reduce<{ [userId: string]: Teach.Class.OrgStudentJSON }>((acc, s) => {
        if (!s.user || s.isArchived) {
            return acc
        }
        acc[s.user.objectId] = s

        return acc
    }, {}) || {}
})

const tableColumnStyles = computed(() => {
    const styles: { [key: string]: string } = {
        backgroundColor: brandColors.grayBackground || '',
        fontSize: '12px',
        paddingRight: '12px',
        color: brandColors.brandBlack || '',
    }
    if (!filteredAssignments.value.length) {
        styles.borderBottom = 'none'
    }
    return styles
})

const mappedAssignments = computed(() => {
    const duplicateAssignments: IMappedAssignment[] = []
    const originalAssignments = Object.values(assignments.value).reduce<IMappedAssignment[]>((acc, assignment) => {
        const styles: { [key: string]: string } = {}
        if (rowHoverId.value === assignment.objectId) {
            styles.backgroundColor = `${brandColors.grayBackground}60`
        }
        const orgExam = Object.values(orgExams.value).find(oe =>
            oe.examMetadata.objectId === assignment.examMetadata.objectId)
        if (!currentOrgInstructor.value?.isAdmin
            && !(orgExam && currentInstructorExams.value.includes(orgExam.objectId))) {
            return acc
        }
        const shareLink = `${import.meta.env.VUE_APP_STUDY_URL}/quiz?assignment=${assignment.objectId}`
        const stats = Object.entries(assignment.attempts || {}).reduce((statsAcc, [ userId, attempt ]) => {
            const orgStudent = userIdOrgStudentLib.value[userId]
            const lastAttempt = attempt[attempt.length - 1]
            if (!orgStudent || orgStudent.isArchived || !orgStudent.user) {
                return statsAcc
            }

            statsAcc.studentCount++
            statsAcc.incorrectCount += lastAttempt?.incorrectCount || 0
            statsAcc.correctCount += lastAttempt?.correctCount || 0
            return statsAcc
        }, { correctCount: 0, incorrectCount: 0, studentCount: 0 })
        const averageScore = (stats.correctCount / ((stats.incorrectCount + stats.correctCount) || 1)) * 100
        const creator = orgInstructorUsers.value?.[assignment.creator.objectId]
        // fire sentry error if no creator name name?
        const creatorName = `${creator?.firstName} ${creator?.lastName}`
        const examName = examModule.getters.getOrgExamForExamMetadataId(assignment.examMetadata.objectId)?.displayName
            || ''
        const customGroups = assignmentIdToCustomGroupsLib.value[assignment.objectId] || []
        const rawAvailableDate = assignment?.availableDate?.iso
        const rawDueDate = assignment?.dueDate?.iso
        const availableDate = assignment.availableDate && formatDate(assignment.availableDate.iso)
        const dueDate = assignment.dueDate && formatDate(assignment.dueDate.iso)
        const isLegacyAssignment = assignment.status !== 'draft' && !assignment.availableDate && !assignment.dueDate
        const status = isLegacyAssignment
            ? 'available'
            : assignment.status === 'draft'
                ? 'draft'
                : getAssignmentStatus({ availableDate: rawAvailableDate, dueDate: rawDueDate })

        const mappedAssignment: IMappedAssignment = {
            id: assignment.objectId,
            name: assignment.name,
            createdBy: creatorName,
            creatorFirstName: creator?.firstName || '',
            creatorLastName: creator?.lastName || '',
            numberOfQuestions: assignment.questionSerials.length,
            studentsCompletedCount: stats.studentCount,
            studentsAssignedCount: assignment.assignedOrgStudents?.length || 0,
            examName,
            customGroups,
            status,
            averageScore: stats.studentCount ? `${Math.round(averageScore)}%` : '--%',
            availableDate,
            dueDate,
            averageScoreNumber: stats.studentCount ? averageScore : -1,
            shareLink,
            styles,
        }

        if (assignment.objectId in indexOfOriginalAssignmentByCloneId.value) {
            duplicateAssignments.push(mappedAssignment)
        } else {
            acc.push(mappedAssignment)
        }
        return acc
    }, []).sort((a, b) => {
        if (sortSettings.value.column && a && b) {
            if (sortSettings.value.column.name === 'Name') {
                return sortSettings.value.direction * a.name.localeCompare(b.name)
            }
            if (sortSettings.value.column.name === 'Exam') {
                return sortSettings.value.direction * a.examName.localeCompare(b.examName)
            }
            if (sortSettings.value.column.name === 'Created by') {
                return sortSettings.value.direction * a.createdBy.localeCompare(b.createdBy)
            }
            if (sortSettings.value.column.name === 'Score') {
                return sortSettings.value.direction * (a.averageScoreNumber - b.averageScoreNumber)
            }
            if (sortSettings.value.column.name === 'Status') {
                return sortSettings.value.direction * a.status.localeCompare(b.status)
            }
            if (sortSettings.value.column.name === 'Available') {
                if (!a.availableDate) {
                    return sortSettings.value.direction * 1
                }
                if (!b.availableDate) {
                    return sortSettings.value.direction * -1
                }
                const dateA = new Date(a.availableDate)
                const dateB = new Date(b.availableDate)
                return sortSettings.value.direction * ((dateB.getTime() - dateA.getTime()))
            }
            if (sortSettings.value.column.name === 'Due') {
                if (!a.dueDate) {
                    return sortSettings.value.direction * 1
                }
                if (!b.dueDate) {
                    return sortSettings.value.direction * -1
                }
                const dateA = new Date(a.dueDate)
                const dateB = new Date(b.dueDate)
                return sortSettings.value.direction * ((dateB.getTime() - dateA.getTime()))
            }
        }

        return -1
    })
    // sort duplicates by highest index to keep original indexes correct
    duplicateAssignments.sort((a, b) =>
        (indexOfOriginalAssignmentByCloneId.value[a.id] || 0) - (indexOfOriginalAssignmentByCloneId.value[b.id] || 0))
    duplicateAssignments.forEach(duplicate => {
        const indexOfOriginal = indexOfOriginalAssignmentByCloneId.value[duplicate.id] as number
        const targetIndex = indexOfOriginal + 1
        originalAssignments.splice(targetIndex, 0, duplicate)
    })
    return originalAssignments
})

const createdByFilterOptions = computed(() => {
    const allAssignmentsCreators = mappedAssignments.value.map(assignment => assignment.createdBy)
    return [ ...new Set(allAssignmentsCreators) ]
})

const examFilterOptions = computed(() => {
    const examsAvailableToCurrentUser = orgExams.value.filter((exam, index, self) =>
        (currentOrgInstructor.value?.isAdmin || currentInstructorExams.value.includes(exam.objectId))
        && index === self.findIndex(e => e.displayName === exam.displayName))
    return examsAvailableToCurrentUser
})

const customGroupsFilterOptions = computed(() => {
    const groupsVisibleToCurrentUser = Object.values(orgGroups.value).filter((g, index, self) => {
        const orgInstructor = g.owner
            && orgInstructors.value?.[g.owner.objectId]

        return (g.type !== 'private' || currentOrgInstructor.value?.objectId === g.owner?.objectId)
            && (currentOrgInstructor.value?.isAdmin || yourOrgExamIds.value.includes(g.orgExam.objectId))
            && orgInstructor?.status !== 'pending'
            && g.type !== 'system'
            && index === self.findIndex(otherG => otherG.name === g.name)
    })

    return groupsVisibleToCurrentUser
})

const assignmentColumns = computed(() => {
    const defaultColumns: ITableColumn[] = [
        {
            name: 'Name',
            propName: 'name',
            styles: {
                width: '112px',
            },
        },
        {
            name: '#?s',
            propName: 'numberOfQuestions',
            styles: {
                width: '40px',
                textAlign: 'right',
            },
            isSortDisabled: true,
        },
        {
            name: 'Completed',
            propName: 'studentsCompleted',
            styles: {
                width: '68px',
                textAlign: 'center',
            },
            isSortDisabled: true,
        },
        {
            name: 'Score',
            propName: 'averageScore',
            styles: {
                width: '50px',
                textAlign: 'center',
            },
        },
        {
            name: 'Available',
            propName: 'availableDate',
            styles: {
                width: '76px',
            },
        },
        {
            name: 'Due',
            propName: 'dueDate',
            styles: {
                width: '72px',
            },
        },
        {
            name: 'Status',
            propName: 'status',
            styles: {
                width: '104px',
            },
        },
    ]

    if (orgInstructors.value && Object.keys(orgInstructors.value).length > 1) {
        defaultColumns.splice(1, 0, {
            name: 'Created by',
            propName: 'createdBy',
            styles: {
                width: '78px',
            },
        })
    }
    if (examFilterOptions.value.length > 1) {
        defaultColumns.splice(2, 0, {
            name: 'Exam',
            propName: 'examName',
            styles: {
                width: '80px',
            },
            labelStyles: {
                textAlign: 'right',
            },
        })
    }
    if (defaultColumns.length === 8 && defaultColumns[0]) {
        defaultColumns[0].styles = { width: '180px' }
    } else if (defaultColumns.length === 7 && defaultColumns[0]) {
        defaultColumns[0].styles = { width: '280px' }
    }

    return defaultColumns
})

const assignmentsFilters = computed(() => {
    const availableFilters: TDropdownCheckboxMenu[] = []
    if (!mappedAssignments.value.length) {
        return []
    }
    if (createdByFilterOptions.value.length > 1) {
        availableFilters.push({
            filterType: 'createdBy',
            filterMenuLabel: 'filter by creator',
            items: createdByFilterOptions.value.map(creator => ({
                id: creator,
                label: creator,
                enabled: activeFilters.value.createdBy.includes(creator),
            })),
            expanded: true,
            allowCollapse: true,
        })
    }
    if (examFilterOptions.value.length > 1) {
        availableFilters.push({
            filterType: 'exam',
            filterMenuLabel: 'filter by exam',
            items: examFilterOptions.value.map(exam => ({
                id: exam.displayName,
                label: exam.displayName,
                enabled: activeFilters.value.exam.includes(exam.displayName),
            })),
            expanded: true,
            allowCollapse: true,
        })
    }
    availableFilters.push({
        filterType: 'customGroup',
        filterMenuLabel: 'filter by custom group',
        items: customGroupsFilterOptions.value.map(customGroup => ({
            id: customGroup.name,
            label: customGroup.name,
            enabled: activeFilters.value.customGroup.includes(customGroup.name),
        })),
        expanded: true,
        allowCollapse: customGroupsFilterOptions.value.length || examFilterOptions.value.length > 1 ? true : false,
        emptyStateMessage: 'You do not have any custom groups yet.',
    })

    return availableFilters
})

const filteredAssignments = computed(() => {
    const currentActiveFilters = activeFilters.value
    return mappedAssignments.value.filter(assignment => {
        if (
            (!currentActiveFilters.createdBy.length || currentActiveFilters.createdBy.includes(assignment.createdBy))
            && (!currentActiveFilters.exam.length || currentActiveFilters.exam.includes(assignment.examName))
            && (!currentActiveFilters.customGroup.length || assignment.customGroups
                .some(groupName => currentActiveFilters.customGroup.includes(groupName)))
        ) {
            return true
        } else {
            return false
        }
    })
})

const pagedFilteredAssignments = computed(() => {
    return filteredAssignments.value.slice(currentPage.value * perPage, (currentPage.value * perPage) + perPage)
})

const assignmentToBeDeletedCompletionStats = computed(() => {
    const assignment = mappedAssignments.value.find(a => a.id === assignmentIdToDelete.value)
    if (!assignment) {
        return null
    }
    return {
        studentsCompletedCount: assignment.studentsCompletedCount,
        studentsNotCompletedCount: assignment.studentsAssignedCount - assignment.studentsCompletedCount,
    }
})

const bundleBrandLib = computed(() => {
    return bundleModule.getters.getBundleBrandLib()
})

const isSortIconVisible = computed(() => {
    return Object.keys(indexOfOriginalAssignmentByCloneId.value).length ? true : false
})

const getCreatorName = (params: { firstName?: string; lastName?: string }) => {
    return `${params.firstName}${params.lastName ? ' ' + params.lastName.charAt(0) : ''}`
}

const copyLink = async (assignmentId?: string) => {
    const assignment = assignmentId ? assignments.value[assignmentId] : storedAssignment.value
    if (!assignment) {
        return
    }
    const examBundleId = bundleModule.getters.getExamBundle(assignment.examMetadata.objectId)?.objectId
    if (!examBundleId) {
        return
    }
    const examBundleLinkName = bundleBrandLib.value[examBundleId]?.universalLinkName || 'web'
    const link = `https://pocketprep.com/apps/${examBundleLinkName}/assignment?id=${assignment.objectId}`

    await navigator.clipboard.writeText(link)

    if (currentOrg.value) {
        analyticsModule.actions.amplitudeTrack('Assignment Link Copied', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            examGuid: examMetadata.value[assignment.examMetadata.objectId]?.examGuid,
            bundleId: bundleModule.getters.getExamBundle(assignment.examMetadata.objectId)?.objectId || '',
            assignedStudentCount: assignment.assignedOrgStudents?.length || 0,
            assignmentName: assignment.name,
        })
    }

    closeAndResetModals()

    toastModule.actions.displayToast({
        title: 'Link copied to clipboard.',
    })

}

const cloneAssignment = async (assignmentId: string) => {
    isLoading.value = true
    errors.value = []

    try {
        const newCopy = await assignmentModule.actions.cloneAssignment({ assignmentId })
        const index = mappedAssignments.value.findIndex(a => a.id === assignmentId)
        indexOfOriginalAssignmentByCloneId.value[newCopy.objectId] = index
        assignmentModule.actions.fetchAssignments()

        closeAndResetModals()

        toastModule.actions.displayToast({
            title: 'Assignment Duplicated.',
        })

        if (currentOrg.value) {
            analyticsModule.actions.amplitudeTrack('Assignment Cloned', {
                organizationId: currentOrg.value.objectId,
                hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
                examGuid: examMetadata.value[newCopy.examMetadata.objectId]?.examGuid,
                bundleId: bundleModule.getters.getExamBundle(newCopy.examMetadata.objectId)?.objectId || '',
                userIsOwnerOfAssignment: assignments.value[assignmentId]?.creator
                    .objectId === currentOrgInstructor.value?.objectId,
                assignmentName: newCopy.name,
                assignmentId: newCopy.objectId,
            })
        }
    } catch (err) {
        errors.value.push('There was a problem duplicating. Please try again.')
    }
    isLoading.value = false
}

const goToAssignment = async (specificAssignmentId?: string) => {
    const assignment = specificAssignmentId ? assignments.value[specificAssignmentId] : storedAssignment.value
    if (!assignment) {
        return
    }
    const assignmentId = assignment.objectId
    if (assignment.status === 'draft') {
        await router.push({
            name: 'edit-assignment',
            params: { assignmentId },
        })
    } else {
        await router.push({
            name: 'view-assignment',
            params: { assignmentId },
        })
    }
    closeAndResetModals()
}

const openAssignmentInStudy = async (specificAssignmentId?: string) => {
    const assignment = specificAssignmentId ? assignments.value[specificAssignmentId] : storedAssignment.value
    if (!assignment) {
        return
    }
    const assignmentId = assignment.objectId
    const studyURL = `${import.meta.env.VUE_APP_STUDY_URL}/assignment?id=${assignmentId}`
    const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
    const sessionToken = getSessionToken()

    if (studyURL && sessionToken && sessionCookieName) {
        setCookie({ name: sessionCookieName, value: sessionToken })
        await userModule.actions.updateWebConfig({ lastActiveApp: 'study' })
        await userModule.actions.trackEvent('App_Switch')
        window.location.href = studyURL
    }
}

const deleteAssignment = async () => {
    isLoading.value = true
    errors.value = []
    const assignmentId = assignmentIdToDelete.value
    const assignment = assignmentIdToDelete.value && assignments.value[assignmentIdToDelete.value]
    if (!assignmentId) {
        return
    }
    try {
        await assignmentModule.actions.deleteAssignment({ assignmentId })
        toastModule.actions.displayToast({
            title: 'Assignment deleted.',
        })
        if (currentOrg.value && assignment) {
            analyticsModule.actions.amplitudeTrack('Assignment Deleted', {
                organizationId: currentOrg.value.objectId,
                hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
                examGuid: examMetadata.value[assignment.examMetadata.objectId]?.examGuid,
                bundleId: bundleModule.getters.getExamBundle(assignment.examMetadata.objectId)?.objectId || '',
                userIsOwnerOfAssignment: assignments.value[assignmentId]?.creator
                    .objectId === currentOrgInstructor.value?.user.objectId,
                assignmentStatus: assignment.status === 'draft'
                    ? 'draft'
                    : getAssignmentStatus({
                        availableDate: assignment.availableDate?.iso,
                        dueDate: assignment.dueDate?.iso,
                    }),
            })
        }
    } catch (err) {
        errors.value.push('There was a problem deleting. Please try again.')
    }
    closeAndResetModals()
    isLoading.value = false
}

const closeAndResetModals = () => {
    assignmentModule.state.createdAssignment = null
    assignmentIdToDelete.value = null
    activeActionsRowId.value = null
    showSuccessModal.value = false
    showDeleteModal.value = false
}

const changePage = (newPage: number) => {
    currentPage.value = newPage

    tableModule.actions.saveTableState({
        name: 'assignments',
        sort: sortSettings.value,
        currentPage: currentPage.value,
        filters: activeFilters.value,
    })
}

const toggleRowActions = (rowId: string) => {
    activeActionsRowId.value = activeActionsRowId.value === rowId ? null : rowId
}

const handleRowAction = (action: TAssignmentAction, rowId: string) => {
    const assignment = assignments.value[rowId]
    if (!assignment) {
        return
    }
    if (action === 'Copy Link') {
        copyLink(rowId)
    } else if (action === 'View/Edit') {
        goToAssignment(rowId)
    } else if (action === 'Duplicate') {
        cloneAssignment(rowId)
    } else if (action === 'Delete') {
        assignmentIdToDelete.value = rowId
        showDeleteModal.value = true
    }
    activeActionsRowId.value = null
}

const updateSortSettings = (newSortSettings: ITableSortSettings) => {
    indexOfOriginalAssignmentByCloneId.value = {}
    sortSettings.value = newSortSettings

    tableModule.actions.saveTableState({
        name: 'assignments',
        sort: sortSettings.value,
        currentPage: currentPage.value,
        filters: activeFilters.value,
    })
}

const updateFilter = (updatedFilter: { filterType: string; item: TCheckboxOption }) => {
    const filterType = updatedFilter.filterType
    const item = updatedFilter.item
    if (filterType in activeFilters.value) {
        const filter = filterType as keyof TActiveFilters
        if (item.enabled) {
            activeFilters.value[filter] = [ ...activeFilters.value[filter], item.id ]
        } else {
            activeFilters.value[filter] = activeFilters.value[filter].filter(itemId => itemId !== item.id)
        }
    }

    if (filteredAssignments.value.length <= perPage) {
        currentPage.value = 0
    }

    tableModule.actions.saveTableState({
        name: 'assignments',
        sort: sortSettings.value,
        currentPage: currentPage.value,
        filters: activeFilters.value,
    })
}

const triggerTableFilteredEvent = () => {
    if (currentOrg.value) {
        analyticsModule.actions.amplitudeTrack('Table Filtered', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            tableName: 'Assignments',
            didFilterInstructor: activeFilters.value.createdBy.length ? true : false,
            instructorFiltersCount: activeFilters.value.createdBy.length,
            didFilterCustomGroup: activeFilters.value.customGroup.length ? true : false,
            customGroupFiltersCount: activeFilters.value.customGroup.length,
            didFilterExam: activeFilters.value.exam.length ? true : false,
            examFiltersCount: activeFilters.value.exam.length,
        })
    }
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

watchEffect(() => {
    // when the only assignment by instructor is deleted, their filter option disappears
    // so we need to remove the active filter as well
    if (!isLoading.value) {
        activeFilters.value.createdBy.forEach((createdByFilter, index) => {
            if (!createdByFilterOptions.value.includes(createdByFilter)) {
                activeFilters.value.createdBy.splice(index, 1)
            }
        })
    }
})

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        groupModule.actions.fetchCurrentOrgGroups(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        assignmentModule.actions.fetchAssignments(),
        bundleModule.actions.fetchBundles(),
        examModule.actions.fetchExamMetadata(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    if (storedAssignment.value) {
        showSuccessModal.value = true
    }

    const assignmentTableState = tableModule.getters.getTableState('assignments')
    if (assignmentTableState) {
        currentPage.value = assignmentTableState.currentPage
        sortSettings.value = assignmentTableState.sort
        activeFilters.value = assignmentTableState.filters as TActiveFilters
    } else {
        const currentInstructorUser = orgInstructorUsers.value && currentOrgInstructor.value
            && orgInstructorUsers.value[currentOrgInstructor.value.user.objectId]
        const currentInstructorName = `${currentInstructorUser?.firstName} ${currentInstructorUser?.lastName}`

        if (orgInstructors.value && Object.keys(orgInstructors.value).length > 1
            && createdByFilterOptions.value.includes(currentInstructorName)) {
            activeFilters.value.createdBy.push(currentInstructorName)
        }
    }

    isLoading.value = false
})

</script>

<template>
    <Portal to="modal">
        <ModalContainer v-if="showSuccessModal" @close="closeAndResetModals">
            <Modal
                class="assignments__modal"
                :showCloseButton="true"
                @close="closeAndResetModals"
            >
                <div class="assignments__modal-container">
                    <div class="assignments__modal-head">Your assignment has been published 🎉</div>
                    <div class="assignments__modal-text-1">
                        Students will see and be able to take the assignment on their
                        Pocket Prep app starting
                        {{ storedAssignment?.availableDate ? formatDate(storedAssignment?.availableDate?.iso) : '' }}.
                    </div>
                    <div class="assignments__modal-text-2">
                        You will be able to copy and paste the assignment link to
                        share with your students via your LMS or email at any time.
                    </div>
                </div>
                <div class="assignments__modal-buttons">
                    <PocketButton
                        type="secondary"
                        @click="openAssignmentInStudy()"
                    >
                        View Student Assignment
                    </PocketButton>
                    <PocketButton @click="copyLink()">
                        Copy Link
                    </PocketButton>
                </div>
            </Modal>
        </ModalContainer>
    </Portal>
    <DeleteAssignmentModal
        v-if="showDeleteModal && assignmentToBeDeletedCompletionStats"
        :assignmentStudentCompletedStats="assignmentToBeDeletedCompletionStats"
        @close="closeAndResetModals"
        @delete="deleteAssignment"
    />
    <div v-if="!isLoading" class="assignments">
        <div class="assignments__title">
            Assignments
            <Tag
                :is-beta-tag="true"
                class="assignments__beta-tag"
                size="large"
            >
                <template #tagMessage>
                    Beta
                </template>
            </Tag>
        </div>
        <div class="assignments__table-menu">
            <div class="assignments__filter">
                <MultiCheckboxFilter
                    :dropdownMenus="assignmentsFilters"
                    :filterLabel="'Filter Assignments'"
                    @updateFilters="updateFilter"
                    @dropdownClosed="triggerTableFilteredEvent"
                />
            </div>
            <div class="assignments__create-btn">
                <PocketButton
                    @click="router.push({ name: 'create-assignment' })"
                    :disabled="!orgLicenses.length"
                >
                    Create New
                </PocketButton>
            </div>
        </div>
        <Errors
            v-if="errors.length"
            :errors="errors"
            class="assignments__error"
        >
            <template #error>
                <div>{{ errors[0] }}</div>
                <Icon
                    type="close"
                    class="assignments__close-error"
                    @click="errors = []"
                />
            </template>
        </Errors>
        <Table
            :columns="assignmentColumns"
            :rows="pagedFilteredAssignments"
            :defaultSort="sortSettings"
            :sortToggleOrder="[ 1, -1 ]"
            :showHeader="false"
            :key="assignmentsFilters.length"
            :tableColumnLabelsStyles="tableColumnStyles"
            :tableColumnLabelStyles="{
                height: '16px',
            }"
            :tableRowStyles="{
                fontSize: '14px',
                paddingRight: '12px',
            }"
            :tableRowsStyles="{
                overflow: 'visible',
                cursor: 'pointer'
            }"
            :tableCellStyles="{
                overflow: 'visible',
                height: '20px'
            }"
            :tableSortIconStyles="{
                display: isSortIconVisible ? 'none' : 'inline-block'
            }"
            @sort="updateSortSettings"
            @rowEnter="rowEnter"
            @rowLeave="rowLeave"
            @rowClicked="goToAssignment($event.id as string)"
        >
            <template #tableCellValue="{ row, column }">
                <template v-if="column.propName === 'createdBy'">
                    <OverflowTooltip class="assignments__created-by-cell">
                        {{ getCreatorName({firstName: row.creatorFirstName, lastName: row.creatorLastName}) }}
                    </OverflowTooltip>
                </template>
                <template v-if="column.propName === 'examName'">
                    <div class="assignments__exam-cell">
                        <OverflowTooltip>
                            {{ row.examName }}
                        </OverflowTooltip>
                    </div>
                </template>
                <template v-if="column.propName === 'studentsCompleted'">
                    <div class="assignments__students-completed-cell">
                        <OverflowTooltip>
                            {{ `${row.studentsCompletedCount || '--'} / ${row.studentsAssignedCount || '--'}` }}
                        </OverflowTooltip>
                    </div>
                </template>
                <template v-if="column.propName === 'availableDate'">
                    <OverflowTooltip>
                        {{ row.availableDate ? row.availableDate : row.status === 'draft' ? '--' : 'N/A' }}
                    </OverflowTooltip>
                </template>
                <template v-if="column.propName === 'dueDate'">
                    <OverflowTooltip>
                        {{ row.dueDate ? row.dueDate : row.status === 'draft' ? '--' : 'N/A' }}
                    </OverflowTooltip>
                </template>
                <template v-if="column.propName === 'status'">
                    <div class="assignments__status-cell">
                        <Tag
                            v-if="row.status === 'draft'"
                            size="small"
                            :isDraftTag="true"
                            :tagBorder="`1px solid ${brandColors.bananaBread}`"
                        >
                            <template #tagMessage>
                                Draft
                            </template>
                        </Tag>
                        <Tag
                            v-else-if="row.status === 'upcoming'"
                            size="small"
                            :isUpcomingTag="true"
                            :tagBorder="`1px solid ${brandColors.spectralGreen}80`"
                        >
                            <template #tagMessage>
                                Upcoming
                            </template>
                        </Tag>
                        <Tag
                            v-else-if="row.status === 'available'"
                            size="small"
                            :isAvailableTag="true"
                        >
                            <template #tagMessage>
                                Available
                            </template>
                        </Tag>
                        <Tag
                            v-else
                            size="small"
                            :isClosedTag="true"
                        >
                            <template #tagMessage>
                                Closed
                            </template>
                        </Tag>
                        <ActionsDropdown
                            :instanceId="row.id"
                            :actions="assignmentActions"
                            :isActive="row.id === activeActionsRowId"
                            :disableCopyLink="row.status === 'draft'"
                            @toggle="toggleRowActions"
                            @action="handleRowAction"
                        >
                        </ActionsDropdown>
                    </div>
                </template>
            </template>
        </Table>
        <div v-if="!pagedFilteredAssignments.length" class="assignments__empty-state-container">
            <EmptyState
                v-if="!orgLicenses.length"
                :title="'Available to organizations with seats'"
                class="assignments__empty-state"
            >
                <template #imageRow>
                    <img
                        class="assignments__empty-state-image"
                        src="@/assets/images/knowledge-drop-books.png"
                    >
                </template>
                <template #emptyStateMessage>
                    Once you purchase seats, you will be able to create assignments for your students.
                    <div class="assignments__learn-link">
                        <PocketLink
                            href="https://help.educators.pocketprep.com/en/articles/11106034-introducing-assignments"
                        >
                            <span>Learn about Assignments </span>
                            <Icon type="externalLink"></Icon>
                        </PocketLink>
                    </div>
                </template>
            </EmptyState>
            <EmptyState
                v-else-if="!mappedAssignments.length"
                :title="'There are no assignments yet'"
                class="assignments__empty-state"
            >
                <template #imageRow>
                    <img
                        class="assignments__empty-state-image"
                        src="@/assets/images/knowledge-drop-books.png"
                    >
                </template>
                <template #emptyStateMessage>
                    Create assignments to standardize your student's quiz experience.
                    <div class="assignments__learn-link">
                        <PocketLink
                            href="https://help.educators.pocketprep.com/en/articles/11106034-introducing-assignments"
                        >
                            <span>Learn about Assignments </span>
                            <Icon type="externalLink"></Icon>
                        </PocketLink>
                    </div>
                </template>
            </EmptyState>
            <EmptyState
                v-else
                :title="'No results'"
                :message="'There are no assignments that match your filter criteria.'"
                class="assignments__empty-state"
            >
                <template #imageRow>
                    <div />
                </template>
            </EmptyState>
        </div>
        <div class="assignments__table-footer">
            <TablePagination
                v-if="pagedFilteredAssignments.length"
                class="assignments__table-pagination"
                :per-page="perPage"
                unit="Assignments"
                :current-page="currentPage"
                :total="filteredAssignments.length"
                @changePage="changePage"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.assignments {
    color: $brand-black;

    &__title {
        font-size: 26px;
        font-weight: 600;
        line-height: 34px;
        margin: 16px 0 54px 22px;
        position: relative;
    }

    &__beta-tag {
        position: relative;
        bottom: 2px;
    }

    &__table-menu {
        display: flex;
        margin-bottom: 8px;
    }

    &__create-btn {
        margin-left: auto;
        flex-shrink: 0;
        position: relative;
        bottom: 16px;
    }

    &__table-footer {
        height: 23px;
        margin-top: 12px;
        display: flex;
        align-items: flex-end;
        justify-content: end;
    }

    &__empty-state-container {
        border: 1px solid rgba($pewter, 0.3);
        border-top: none;
    }

    &__empty-state {
        margin: 0 auto;
        padding: 40px 0;
        min-width: 300px;
    }

    &__empty-state-image {
        width: 65px;
        height: 92px;
        margin-bottom: 22px;
    }

    &__created-by-cell {
        height: 20px;
    }

    &__learn-link {
        margin-top: 22px;
    }

    &__status-cell {
        display: flex;
        justify-content: space-between;
        height: 20px;
        align-items: center;

        :deep(.actions) {
            top: 2px;
        }
    }

    &__modal {
        width: 374px;
        padding-bottom: 24px;
    }

    &__modal-container {
        padding: 36px 40px 0;
        text-align: center;
        margin-bottom: 32px;
    }

    &__modal-head {
        font-size: 18px;
        line-height: 25px;
        letter-spacing: 0.3px;
        font-weight: 600;
        margin-bottom: 12px;
    }

    &__modal-text-1, &__modal-text-2 {
        color: $ash;
        font-size: 15px;
        line-height: 20px;
    }

    &__modal-text-2 {
        margin-top: 20px;
    }

    &__modal-buttons {
        justify-content: space-between;
        display: flex;
        padding: 0 23.5px;

        &--delete-modal {
            margin: auto;
            width: 260px;
        }
    }

    &__error {
        margin-bottom: 12px;
        position: relative;
    }

    &__close-error {
        position: absolute;
        right: 12px;
        bottom: 8px;
        color: $steel;

        &:hover {
            cursor: pointer;
        }
    }

    :deep(table.uikit-table__content) {
        border: 1px solid rgba($pewter, 0.3);
        border-radius: 4px;
    }

    :deep(.uikit-table__row:last-child) {
        border-bottom: none;
    }
}
</style>
